import { gql } from '@apollo/client';
import { Form, Select, Slider, Input, Alert, Row, InputNumber, Col, Checkbox } from 'antd';
import Modal from 'components/Modal';
import { useLazyQuery, useMutation } from 'hooks/apollo';
import useUser from 'hooks/useUser';
import React, { useEffect, useState } from 'react';

const { TextArea } = Input;

const temperatureMarks = {
  0: {
    style: {
      color: '#05f',
    },
    label: <strong>0</strong>,
  },
  1: '1',
  2: {
    style: {
      color: '#f50',
    },
    label: <strong>2</strong>,
  },
};

const autoTagDefaultValues = {
  temperature: 0.5,
  min: 3,
  max: 15,
};

const SUGGEST_TAGS = gql`
  query($input: AutoSuggestTagsInput!) {
    autoSuggestTags(input: $input) {
      name
      relevance
      frequency
    }
  }
`;

const CREATE_TAGS = gql`
  mutation($input: AddThemesInput!) {
    addThemes(input: $input) {
      id
    }
  }
`;

const AutoSuggestTags = ({ open, onCancel, onDone, tags, questionIds, comments }) => {
  const { isSuperUser } = useUser();
  const [form] = Form.useForm();
  const [suggestTags, { loading }] = useLazyQuery(SUGGEST_TAGS);
  const [createTags, { loading: creating }] = useMutation(CREATE_TAGS);
  const [suggestions, setSuggestions] = useState([]);
  const [step, setStep] = useState(1);

  useEffect(() => {
    setStep(1);
    form.resetFields();
  }, [open]);

  const initialValues = {
    ...autoTagDefaultValues,
    tagIds: tags.map((tag) => tag.id),
  };

  const onSubmit = async (values) => {
    if (step === 1) {
      const input = {
        temperature: values.temperature,
        min: values.min,
        max: values.max,
        examples: tags.map((tag) => tag.text),
        context: comments.slice(0, 100).map((comment) => comment.text).join('\n***\n'),
        customPrompt: `The data consists of survey responses and each message is separated with ***\n${values.customPrompt}`,
      };
      const result = await suggestTags({ variables: { input } });

      setSuggestions(result.data.autoSuggestTags.toSorted((a, b) => b.relevance - a.relevance));
      setStep(2);
    }
    if (step === 2) {
      await createTags({ variables: { input: { themes: values.suggestions, questionIds } } });
      onDone();
    }
  };

  const handleCancel = () => {
    if (step > 1) {
      setStep(1);
    } else {
      onCancel();
    }
  };

  const okText = step > 1 ? 'Create selected tags' : 'Generate';
  const cancelText = step > 1 ? 'Back' : 'Cancel';

  return (
    <Modal open={open} onCancel={handleCancel} title="Suggest tags" okText={okText} cancelText={cancelText} onOk={form.submit} confirmLoading={loading || creating}>
      <Form form={form} layout="vertical" initialValues={initialValues} onFinish={onSubmit}>
        {step === 1 && (
        <>
          <Form.Item>
            <Alert message="This will send the text responses to an external service. It can take a while to load the generated tags." type="warning" showIcon />
          </Form.Item>
          <Form.Item name="tagIds" label="Tags to include as examples">
            <Select options={tags.map((theme) => ({ value: theme.id, label: theme.text }))} mode="multiple" allowClear />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="temperature" label="Temperature">
                <Slider marks={temperatureMarks} min={0} max={2} step={0.1} />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="min" label="Min tags">
                <InputNumber min={1} max={15} />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="max" label="Max tags">
                <InputNumber min={1} max={15} />
              </Form.Item>
            </Col>
          </Row>
          {isSuperUser && (
          <Form.Item name="customPrompt" label="Custom instructions">
            <TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
          )}
        </>
        )}
        {step === 2 && (
        <Form.Item label="Suggestions (occurence)" name="suggestions">
          <Checkbox.Group className="checkbox-group-vertical" options={suggestions.map((suggestion) => ({ label: `${suggestion.name}  (${suggestion.frequency})`, value: suggestion.name }))} />
        </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AutoSuggestTags;
